import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import AdminBulletinUpload from '../components/AdminBulletinUpload';
import AdminProgramUpload from '../components/AdminProgramUpload';
import AdminBannerManager from '../components/AdminBannerManager';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;
`;

const AdminHeader = styled.div`
  background: #fff;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  h1 {
    color: #2c5aa0;
    margin-bottom: 10px;
  }

  p {
    color: #666;
  }
`;

const AdminGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const AdminCard = styled.div`
  background: #fff;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  h3 {
    color: #2c5aa0;
    margin-bottom: 15px;
    font-size: 20px;
  }

  .stats {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .stat {
    text-align: center;
    
    .number {
      font-size: 24px;
      font-weight: 700;
      color: #2c5aa0;
    }
    
    .label {
      font-size: 14px;
      color: #666;
    }
  }
`;

const ActionButton = styled.button`
  background: #2c5aa0;
  color: #fff;
  border: none;
  padding: 12px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: background 0.3s ease;

  &:hover {
    background: #1e3a8a;
  }

  &.secondary {
    background: #6c757d;
    
    &:hover {
      background: #545b62;
    }
  }

  &.success {
    background: #28a745;
    
    &:hover {
      background: #1e7e34;
    }
  }
`;

const UploadSection = styled.div`
  background: #fff;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;

  h3 {
    color: #2c5aa0;
    margin-bottom: 20px;
    font-size: 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #333;
  }

  input, textarea, select {
    width: 100%;
    padding: 12px;
    border: 2px solid #e9ecef;
    border-radius: 5px;
    font-size: 14px;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2c5aa0;
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const FileUpload = styled.div`
  border: 2px dashed #e9ecef;
  border-radius: 5px;
  padding: 30px;
  text-align: center;
  transition: border-color 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: #2c5aa0;
  }

  input[type="file"] {
    display: none;
  }

  .upload-text {
    color: #666;
    margin-bottom: 10px;
  }

  .upload-icon {
    font-size: 40px;
    color: #2c5aa0;
    margin-bottom: 10px;
  }
`;

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [news, setNews] = useState<any[]>([]);
  const [newsCount, setNewsCount] = useState(0);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    category: 'news'
  });

  const API_URL = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';

  const fetchNews = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/news`);
      const data = await response.json();
      if (data.success && data.news) {
        setNews(data.news);
        setNewsCount(data.news.length);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [API_URL]);

  // Check if user is already logged in on component mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNews();
    }
  }, [isAuthenticated, fetchNews]);

  const handleAnnouncementSubmit = async () => {
    if (!announcementForm.title) return alert('Please enter a title');

    try {
      // Simple date formatter (MM.DD)
      const today = new Date();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const dateStr = `${mm}.${dd}`;

      const payload = {
        title: announcementForm.title,
        category: announcementForm.category,
        date: dateStr,
        link: '/news',
        content: announcementForm.content
      };

      const token = localStorage.getItem('adminToken') || 'dummy-token';

      const response = await fetch(`${API_URL}/api/news`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        alert('Announcement uploaded successfully');
        setAnnouncementForm({ title: '', content: '', category: 'news' });
        fetchNews(); // Refresh list
      } else {
        alert('Failed to upload: ' + data.error);
      }
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Error uploading announcement');
    }
  };

  const handleDeleteNews = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      const token = localStorage.getItem('adminToken') || 'dummy-token';
      const response = await fetch(`${API_URL}/api/news/${id}`, {
        method: 'DELETE',
        headers: {
          'x-api-key': token
        }
      });
      const data = await response.json();
      if (data.success) {
        fetchNews();
      } else {
        alert('Failed to delete');
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // Dynamic Stats
  const stats = {
    announcements: newsCount,
    photos: 45, // Still hardcoded for now, as requested focusing on Announcements
    programs: 8,
    users: 156
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();

    // Get credentials from environment variables
    const validUsername = process.env.REACT_APP_ADMIN_USERNAME || 'admin';
    const validPassword = process.env.REACT_APP_ADMIN_PASSWORD || 'admin2025';
    const prodApiKey = process.env.REACT_APP_PROD_API_KEY || '';
    const devApiKey = process.env.REACT_APP_DEV_API_KEY || '';

    // Map user-friendly passwords to API keys
    const passwordMap: { [key: string]: string } = {
      [validPassword]: prodApiKey, // Production API key
      'dev2025': devApiKey // Development API key
    };

    if (username === validUsername && passwordMap[password]) {
      setIsAuthenticated(true);
      // Store the mapped API key
      localStorage.setItem('adminToken', passwordMap[password]);
    } else {
      alert('Invalid username or password');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Handle file upload logic here
      console.log('File uploaded:', file.name);
    }
  };

  if (!isAuthenticated) {
    return (
      <AdminContainer>
        <div style={{ maxWidth: '400px', margin: '100px auto' }}>
          <AdminHeader>
            <h1>Admin Login</h1>
            <p>Please login to access the admin panel</p>
          </AdminHeader>

          <form onSubmit={handleLogin}>
            <FormGroup>
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
              />
            </FormGroup>

            <FormGroup>
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
            </FormGroup>

            <ActionButton type="submit">Login</ActionButton>
          </form>

          <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
            Default credentials configured in .env file
          </p>
        </div>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminHeader>
        <h1>Admin Dashboard</h1>
        <p>Welcome back, Administrator</p>
        <ActionButton
          className="secondary"
          onClick={() => {
            setIsAuthenticated(false);
            localStorage.removeItem('adminToken');
            setUsername('');
            setPassword('');
          }}
          style={{ marginTop: '10px' }}
        >
          Logout
        </ActionButton>
      </AdminHeader>

      <div style={{ marginBottom: '20px' }}>
        <ActionButton
          onClick={() => setActiveTab('dashboard')}
          style={{ marginRight: '10px' }}
        >
          Dashboard
        </ActionButton>
        <ActionButton
          className="secondary"
          onClick={() => setActiveTab('upload')}
          style={{ marginRight: '10px' }}
        >
          Upload Content
        </ActionButton>
        <ActionButton
          className="success"
          onClick={() => setActiveTab('manage')}
        >
          Manage Content
        </ActionButton>
      </div>

      {activeTab === 'dashboard' && (
        <>
          <AdminGrid>
            <AdminCard>
              <h3>공지사항 관리</h3>
              <div className="stats">
                <div className="stat">
                  <div className="number">{stats.announcements}</div>
                  <div className="label">Total</div>
                </div>
              </div>
              <ActionButton onClick={() => navigate('/admin/news')}>
                공지사항 추가 / 수정
              </ActionButton>
            </AdminCard>

            <AdminCard>
              <h3>사진/영상 관리</h3>
              <div className="stats">
                <div className="stat">
                  <div className="number">📷</div>
                  <div className="label">Gallery</div>
                </div>
              </div>
              <ActionButton onClick={() => navigate('/admin/media')}>
                사진/영상 추가 / 수정
              </ActionButton>
            </AdminCard>

            <AdminCard>
              <h3>Programs</h3>
              <div className="stats">
                <div className="stat">
                  <div className="number">{stats.programs}</div>
                  <div className="label">Active</div>
                </div>
                <div className="stat">
                  <div className="number">2</div>
                  <div className="label">Upcoming</div>
                </div>
              </div>
              <ActionButton>Manage Programs</ActionButton>
            </AdminCard>

            <AdminCard>
              <h3>Members</h3>
              <div className="stats">
                <div className="stat">
                  <div className="number">{stats.users}</div>
                  <div className="label">Total</div>
                </div>
                <div className="stat">
                  <div className="number">8</div>
                  <div className="label">New This Month</div>
                </div>
              </div>
              <ActionButton>View Members</ActionButton>
            </AdminCard>
          </AdminGrid>
        </>
      )}

      {activeTab === 'upload' && (
        <>
          <AdminBulletinUpload />
          <AdminBannerManager />

          <UploadSection>
            <h3>Upload Announcement</h3>
            <FormGroup>
              <label>Title</label>
              <input
                type="text"
                placeholder="Enter announcement title"
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <label>Content</label>
              <textarea
                placeholder="Enter announcement content"
                value={announcementForm.content}
                onChange={(e) => setAnnouncementForm({ ...announcementForm, content: e.target.value })}
              ></textarea>
            </FormGroup>
            <FormGroup>
              <label>Category</label>
              <select
                value={announcementForm.category}
                onChange={(e) => setAnnouncementForm({ ...announcementForm, category: e.target.value })}
              >
                <option value="news">교회 소식 (News)</option>
                <option value="bulletin">주보 (Bulletin)</option>
                <option value="event">행사 (Event)</option>
              </select>
            </FormGroup>
            <ActionButton className="success" onClick={handleAnnouncementSubmit}>Publish Announcement</ActionButton>
          </UploadSection>

          <UploadSection>
            <h3>Upload Photo</h3>
            <FormGroup>
              <label>Photo Title</label>
              <input type="text" placeholder="Enter photo title" />
            </FormGroup>
            <FormGroup>
              <label>Description</label>
              <textarea placeholder="Enter photo description"></textarea>
            </FormGroup>
            <FormGroup>
              <label>Category</label>
              <select>
                <option>Church Events</option>
                <option>Worship</option>
                <option>Community</option>
                <option>Youth</option>
              </select>
            </FormGroup>
            <FileUpload>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                id="photo-upload"
              />
              <label htmlFor="photo-upload">
                <div className="upload-icon">📸</div>
                <div className="upload-text">Click to upload photo</div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  Supports: JPG, PNG, GIF (Max 5MB)
                </div>
              </label>
            </FileUpload>
            <ActionButton className="success" style={{ marginTop: '15px' }}>
              Upload Photo
            </ActionButton>
          </UploadSection>

          <AdminProgramUpload />
        </>
      )}

      {activeTab === 'manage' && (
        <div style={{ background: '#fff', padding: '25px', borderRadius: '10px' }}>
          <h3 style={{ color: '#2c5aa0', marginBottom: '20px' }}>Manage Announcements</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {news.length === 0 ? (
              <p>No announcements found.</p>
            ) : (
              news.map((item) => (
                <div key={item.id} style={{
                  border: '1px solid #eee',
                  padding: '15px',
                  borderRadius: '5px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      background: item.category === 'news' ? '#e3f2fd' : '#fff3e0',
                      color: item.category === 'news' ? '#1565c0' : '#ef6c00',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginBottom: '5px',
                      marginRight: '10px'
                    }}>
                      {item.category}
                    </span>
                    <strong style={{ fontSize: '16px' }}>{item.title}</strong>
                    <div style={{ color: '#888', fontSize: '13px', marginTop: '5px' }}>
                      {item.date}
                    </div>
                  </div>
                  <ActionButton
                    style={{ background: '#ef5350', padding: '8px 15px', fontSize: '12px' }}
                    onClick={() => handleDeleteNews(item.id)}
                  >
                    Delete
                  </ActionButton>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </AdminContainer>
  );
};

export default Admin; 